import { Button } from '@zeiss/beyond-online-react';
import {
  Box,
  ButtonGroup,
  PageHeader,
  StepItem,
  Stepper,
} from '@zeiss/lib-components';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';
import { useNavigate } from 'react-router-dom';

import {
  ApplicationListName,
  Applications,
  FormState,
  Owners,
  Summary,
} from './';
import { showToast } from 'src/helpers/showToast';
import {
  useCreateApplicationList,
  useValidateCreateApplicationList,
} from 'src/hooks/api';
import { ProblemDetails } from 'src/types/rest/tenant/schema';

const formSteps = ['name', 'applications', 'owners', 'summary'] as const;
type FormStep = (typeof formSteps)[number];

const initialState: FormState = {
  applications: [],
  name: '',
  owners: [],
};

const validateStep = (step: FormStep, formState: FormState) => {
  if (formState.error) return false;

  switch (step) {
    case 'name': {
      return formState.name.length > 0;
    }
    case 'applications': {
      return formState.applications.length > 0;
    }
    case 'owners': {
      return formState.owners.length > 0;
    }
    default: {
      return true;
    }
  }
};

export const ApplicationListCreate = () => {
  const { t } = useTranslation(['common']);
  const navigate = useNavigate();
  const { user } = useAuth();

  const [stepIndex, setStepIndex] = React.useState(0);
  const [formState, setFormState] = React.useState(initialState);

  const { isPending: isCreatePending, mutate: createApplicationList } =
    useCreateApplicationList();
  const { isPending: isValidatePending, mutate: validate } =
    useValidateCreateApplicationList();

  const goToNextStep = (event: React.MouseEvent<HTMLButtonElement>) => {
    const submitBtn = event.currentTarget;

    const advance = () => {
      setStepIndex(stepIndex + 1);

      submitBtn.blur();
    };

    // validate Application list name step
    if (stepIndex === 0) {
      const { name } = formState;

      /* istanbul ignore else: User is logged in -- @preserve */
      if (user) {
        validate(
          {
            name,
            ownerIds: [user.profile.sub],
          },
          {
            async onError(error) {
              if (error instanceof Response && error.status === 400) {
                const problemDetails: ProblemDetails = await error.json();
                const errors = problemDetails.errors as Record<
                  string,
                  string[]
                >;
                const nameErrors = errors['Name'];

                if (
                  nameErrors?.includes('Application List name must be unique')
                ) {
                  return setFormState((prev) => ({
                    ...prev,
                    error: t('error.applicationListExists'),
                  }));
                }
              }

              showToast({
                props: {
                  description: t('text.pleaseTryAgain'),
                  title: t('notification.somethingWentWrong'),
                  type: 'error',
                },
              });
            },
            onSuccess() {
              advance();
            },
          },
        );
      }
      // Handle other steps
    } else if (stepIndex < formSteps.length - 1) {
      advance();
    }
  };

  const goToPreviousStep = (event: React.MouseEvent<HTMLButtonElement>) => {
    /* istanbul ignore else -- @preserve */
    if (stepIndex > 0) {
      setStepIndex(stepIndex - 1);
    }

    event.currentTarget.blur();
  };

  const cancel = () => {
    navigate('..', { relative: 'path' });
  };

  const onSubmit = () => {
    const { applications, name, owners } = formState;

    createApplicationList(
      {
        applicationIds: applications.map(({ id }) => id),
        name,
        ownerIds: owners.map(({ id }) => id),
      },
      {
        onError: () => {
          showToast({
            props: {
              title: t('common:notification.errorX', {
                action: t('common:action.created'),
                x: t('common:glossary.applicationList'),
              }),
              type: 'error',
            },
          });
        },
        onSuccess: ({ id }) => {
          showToast({
            props: {
              title: t('common:notification.successX', {
                action: t('common:action.created'),
                x: t('common:glossary.applicationList'),
              }),
              type: 'success',
            },
          });
          navigate(`../${id}`, { relative: 'path' });
        },
      },
    );
  };

  const isStepValid = validateStep(formSteps[stepIndex], formState);

  const isFirstStep = stepIndex === 0;
  const isLastStep = stepIndex === formSteps.length - 1;

  return (
    <>
      <PageHeader
        headline={
          isLastStep
            ? formState.name
            : t('common:action.newX', {
                x: t('common:glossary.applicationList'),
              })
        }
      />

      <Box spacing="2xl">
        <Stepper
          activeStepIndex={stepIndex}
          completedStepIndex={stepIndex - 1}
          onStepRequest={setStepIndex}
          responsiveness="optimal"
          sticky
          variant="numeric"
        >
          <StepItem label={t('common:name')}>
            <ApplicationListName setState={setFormState} state={formState} />
          </StepItem>

          <StepItem label={t('common:glossary.application_other')}>
            <Applications setState={setFormState} state={formState} />
          </StepItem>

          <StepItem label={t('common:glossary.owner_other')}>
            <Owners setState={setFormState} state={formState} />
          </StepItem>

          <StepItem label={t('common:summary')}>
            <Summary state={formState} />
          </StepItem>
        </Stepper>
      </Box>

      <ButtonGroup spacing="s" variant="form">
        <Button
          onClick={isFirstStep ? cancel : goToPreviousStep}
          variant="secondary"
        >
          {isFirstStep ? t('common:action.cancel') : t('common:action.back')}
        </Button>
        <Button
          disabled={!isStepValid}
          loading={isValidatePending || isCreatePending}
          onClick={isLastStep ? onSubmit : goToNextStep}
        >
          {isLastStep ? t('common:action.save') : t('common:action.next')}
        </Button>
      </ButtonGroup>
    </>
  );
};
